import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

const ROLE_HIERARCHY = { admin: 7, hr_admin: 6, website_admin: 5, director: 4, manager: 3, supervisor: 2, employee: 1 };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const [userRes, permRes] = await Promise.all([
            axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${storedToken}` } }),
            axios.get(`${API}/admin/my-permissions`, { headers: { Authorization: `Bearer ${storedToken}` } }).catch(() => ({ data: { permissions: [] } }))
          ]);
          setUser(userRes.data);
          setToken(storedToken);
          setPermissions(permRes.data.permissions || []);
        } catch {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    // Fetch permissions
    try {
      const permRes = await axios.get(`${API}/admin/my-permissions`, { headers: { Authorization: `Bearer ${access_token}` } });
      setPermissions(permRes.data.permissions || []);
    } catch { setPermissions([]); }
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setPermissions([]);
  };

  const getAuthHeader = () => token ? { Authorization: `Bearer ${token}` } : {};

  const roleLevel = (role) => ROLE_HIERARCHY[role] || 1;

  const isAdmin = user?.role === 'admin';
  const isHRAdmin = user?.role === 'hr_admin';
  const isDirector = user?.role === 'director';
  const isManagerRole = user?.role === 'manager';
  const isSupervisor = user?.role === 'supervisor';
  const isEmployee = user?.role === 'employee';
  const canManage = isAdmin || isHRAdmin || user?.role === 'hr_manager' || user?.role === 'website_admin';
  const isManager = user?.is_manager || canManage || isDirector || isManagerRole;

  const hasPermission = (perm) => permissions.includes(perm) || isAdmin;

  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const [userRes, permRes] = await Promise.all([
          axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${storedToken}` } }),
          axios.get(`${API}/admin/my-permissions`, { headers: { Authorization: `Bearer ${storedToken}` } }).catch(() => ({ data: { permissions: [] } }))
        ]);
        setUser(userRes.data);
        setPermissions(permRes.data.permissions || []);
      } catch { /* ignore */ }
    }
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout, getAuthHeader, refreshUser,
      isAdmin, isHRAdmin, isDirector, isManagerRole, isSupervisor, isEmployee,
      canManage, isManager, isAuthenticated: !!user,
      permissions, hasPermission, roleLevel, ROLE_HIERARCHY
    }}>
      {children}
    </AuthContext.Provider>
  );
};
